/**
 * Patient360DynamicLayout.tsx — Fully dynamic, Form Builder–driven layout
 * for Patient360 overview. Replaces hardcoded sections with smart sections
 * resolved from backend metadata + priority scoring.
 *
 * No hardcoded section keys or field names in JSX.
 * Layout driven entirely by presentation profile + resolved sections.
 */

import { memo, useMemo } from 'react';
import type {
  Patient360ResolvedSection,
  Patient360Layout,
  DynamicEntityData,
  DynamicAlertViewModel,
  DynamicLifecycleViewModel,
  DynamicKpiCardViewModel,
} from '../../types/dynamicEntityView';
import { DynamicZoneRenderer } from '../dynamic-view/DynamicZoneRenderer';
import { Patient360SmartSectionGrid } from './Patient360SmartSectionGrid';
import {
  sortSectionsByPriority,
  filterVisibleSections,
  extractPrioritySignals,
} from '../../utils/patient360PriorityEngine';

// ─── Types ──────────────────────────────────────────────────

interface Patient360DynamicLayoutProps {
  /** Resolved sections from backend (with fields + priority scores) */
  resolvedSections: Patient360ResolvedSection[];
  /** Layout configuration from extended profile */
  layout: Patient360Layout;
  /** Dynamic entity data for zone-based field rendering */
  patientEntity?: DynamicEntityData;
  /** Alerts for priority re-scoring */
  alerts: DynamicAlertViewModel[];
  /** Lifecycle for priority context */
  lifecycle: DynamicLifecycleViewModel;
  /** KPIs for priority context */
  kpis: DynamicKpiCardViewModel[];
  /** Whether patient has an active case */
  hasActiveCase: boolean;
  /** Active case cycle number */
  activeCycleNumber?: number;
  /** Remaining cap */
  remainingCap?: number | null;
  /** Cycle end date */
  cycleEnd?: string | null;
  /** Case expiry date */
  caseExpiry?: string | null;
  /** Last activity date */
  lastActivityAt?: string | null;
  /** Program ID for collapse persistence */
  programId?: string;
  /** Patient ID for collapse persistence */
  patientId?: string;
  /** Section keys hidden by workflow/role rules */
  hiddenSectionKeys?: Set<string>;
}

// ─── Default layout fallback ────────────────────────────────

const DEFAULT_LAYOUT: Patient360Layout = {
  headerZones: ['header_identity', 'header_context'],
  topZones: ['clinical_journey', 'next_action'],
  mainGrid: {
    columns: 2,
    leftZones: ['main_left'],
    rightZones: ['main_right'],
    fullWidthZones: ['full_width'],
  },
};

// ─── Component ───────────────────────────────────────────────

export const Patient360DynamicLayout = memo(function Patient360DynamicLayout({
  resolvedSections,
  layout,
  patientEntity,
  alerts,
  lifecycle,
  kpis,
  hasActiveCase,
  activeCycleNumber,
  remainingCap,
  cycleEnd,
  caseExpiry,
  lastActivityAt,
  programId,
  patientId,
  hiddenSectionKeys,
}: Patient360DynamicLayoutProps) {
  const effectiveLayout = layout ?? DEFAULT_LAYOUT;

  // Extract priority signals from current state
  const signals = useMemo(
    () =>
      extractPrioritySignals({
        alerts,
        lifecycle,
        kpis,
        hasActiveCase,
        activeCycleNumber,
        remainingCap,
        cycleEnd,
        caseExpiry,
        lastActivityAt,
      }),
    [alerts, lifecycle, kpis, hasActiveCase, activeCycleNumber, remainingCap, cycleEnd, caseExpiry, lastActivityAt],
  );

  // Re-score sections with client-side signals and filter
  const visibleSections = useMemo(() => {
    const scored = sortSectionsByPriority(resolvedSections, signals);
    return filterVisibleSections(scored, {
      hideEmpty: true,
      hiddenSectionKeys,
    });
  }, [resolvedSections, signals, hiddenSectionKeys]);

  return (
    <div className="p-3 space-y-3">
      {/* Dynamic clinical_snapshot zone (if available from form builder metadata) */}
      {patientEntity && (
        <DynamicZoneRenderer
          zoneKey="clinical_snapshot"
          entityData={patientEntity}
          renderMode="cards"
          columns={3}
        />
      )}

      {/* Dynamic financial_summary zone */}
      {patientEntity && (
        <DynamicZoneRenderer
          zoneKey="financial_summary"
          entityData={patientEntity}
          renderMode="cards"
          columns={3}
        />
      )}

      {/* Smart Section Grid (left/right/full-width) */}
      <Patient360SmartSectionGrid
        sections={visibleSections}
        layout={effectiveLayout}
        programId={programId}
        patientId={patientId}
      />
    </div>
  );
});
